(() => {
  if (window.Torras24HAvailability) return;

  const configElement = document.querySelector(
    "[data-24h-availability-config]",
  );
  let config = {};

  try {
    config = JSON.parse(configElement?.textContent || "{}");
  } catch {
    config = {};
  }

  const availabilityQuery = `
    query Delivery24HAvailability($ids: [ID!]!, $inventoryKey: String!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          localStock: metafield(namespace: "torras_inventory", key: $inventoryKey) {
            value
          }
        }
      }
    }
  `;

  class Delivery24HAvailabilityError extends Error {
    constructor(code, message) {
      super(message);
      this.name = "Delivery24HAvailabilityError";
      this.code = code;
    }
  }

  class Delivery24HAvailability {
    constructor(options) {
      this.endpoint = String(options.endpoint || "");
      this.token = String(options.token || "");
      this.locationGid = String(options.locationGid || "");
      this.batchSize = Math.max(1, Math.min(25, Number(options.batchSize) || 25));
      this.cacheTtl = Math.max(0, Number(options.cacheTtl) || 20000);
      this.requestTimeout = Math.max(
        1000,
        Number(options.requestTimeout) || 8000,
      );
      this.inventoryKey = /^gid:\/\/shopify\/Location\/\d+$/.test(this.locationGid)
        ? `available_${this.locationGid.split("/").pop()}`
        : "";
      this.cache = new Map();
      this.pending = new Map();
    }

    get configured() {
      return (
        Boolean(this.endpoint && this.token && this.inventoryKey)
      );
    }

    variantGid(id) {
      const value = String(id || "").trim();
      if (!value) return "";
      if (value.startsWith("gid://shopify/ProductVariant/")) return value;
      if (!/^\d+$/.test(value)) return "";
      return `gid://shopify/ProductVariant/${value}`;
    }

    variantId(gid) {
      return String(gid || "").split("/").pop() || "";
    }

    quantityAtLocation(variant) {
      // This field mirrors Available at the configured location via Shopify Flow.
      // Never substitute total inventory or pickup availability for local stock.
      const value = variant?.localStock?.value;
      if (!/^-?\d+$/.test(String(value ?? ""))) return 0;
      const quantity = Number(value);
      return Number.isSafeInteger(quantity) ? Math.max(0, quantity) : 0;
    }

    async check(ids, options = {}) {
      if (!this.configured) {
        throw new Delivery24HAvailabilityError(
          "not_configured",
          "24H availability is not configured.",
        );
      }

      const force = options.force === true;
      const signal = options.signal;
      const uniqueIds = [
        ...new Set(
          (Array.isArray(ids) ? ids : [ids])
            .map((id) => this.variantGid(id))
            .filter(Boolean),
        ),
      ];
      const result = new Map();
      const missing = [];
      const now = Date.now();

      uniqueIds.forEach((gid) => {
        const id = this.variantId(gid);
        const cached = this.cache.get(id);
        if (!force && cached && cached.expiresAt > now) {
          result.set(id, cached.quantity);
        } else {
          missing.push(gid);
        }
      });

      const chunks = [];
      for (let index = 0; index < missing.length; index += this.batchSize) {
        chunks.push(missing.slice(index, index + this.batchSize));
      }

      for (let index = 0; index < chunks.length; index += 2) {
        const pair = chunks.slice(index, index + 2);
        const responses = await Promise.all(
          pair.map((chunk) => this.queryChunk(chunk, signal, force)),
        );
        responses.forEach((quantities) => {
          quantities.forEach((quantity, id) => result.set(id, quantity));
        });
      }

      uniqueIds.forEach((gid) => {
        const id = this.variantId(gid);
        if (!result.has(id)) result.set(id, 0);
      });

      return result;
    }

    async queryChunk(gids, signal, force) {
      const key = `${force ? "force:" : ""}${[...gids].sort().join(",")}`;
      if (!force && this.pending.has(key)) return this.pending.get(key);

      const request = this.fetchChunk(gids, signal).finally(() => {
        this.pending.delete(key);
      });
      if (!force) this.pending.set(key, request);
      return request;
    }

    async fetchChunk(gids, signal) {
      const payload = await this.fetchGraphQL(
        availabilityQuery,
        { ids: gids, inventoryKey: this.inventoryKey },
        signal,
      );
      const quantities = new Map();
      const expiresAt = Date.now() + this.cacheTtl;

      (payload.data?.nodes || []).forEach((node) => {
        if (!node?.id) return;
        const id = this.variantId(node.id);
        const quantity = this.quantityAtLocation(node);
        quantities.set(id, quantity);
        this.cache.set(id, { quantity, expiresAt });
      });

      gids.forEach((gid) => {
        const id = this.variantId(gid);
        if (quantities.has(id)) return;
        quantities.set(id, 0);
        this.cache.set(id, { quantity: 0, expiresAt });
      });

      return quantities;
    }

    async fetchGraphQL(query, variables, signal) {
      const requestController = new AbortController();
      let timedOut = false;
      const abortRequest = () => requestController.abort();
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        requestController.abort();
      }, this.requestTimeout);

      if (signal?.aborted) requestController.abort();
      else signal?.addEventListener("abort", abortRequest, { once: true });

      let response;
      try {
        response = await fetch(this.endpoint, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": this.token,
          },
          body: JSON.stringify({
            query,
            variables,
          }),
          signal: requestController.signal,
        });
      } catch (error) {
        if (timedOut) {
          throw new Delivery24HAvailabilityError(
            "request_timeout",
            "Unable to confirm local inventory.",
          );
        }
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
        signal?.removeEventListener("abort", abortRequest);
      }

      if (!response.ok) {
        throw new Delivery24HAvailabilityError(
          "request_failed",
          "Unable to confirm local inventory.",
        );
      }

      const payload = await response.json();
      if (Array.isArray(payload.errors) && payload.errors.length) {
        throw new Delivery24HAvailabilityError(
          "query_failed",
          "Unable to confirm local inventory.",
        );
      }

      return payload;
    }
  }

  window.Torras24HAvailability = new Delivery24HAvailability(config);
  document.dispatchEvent(
    new CustomEvent("torras:24h-availability-ready", {
      detail: { configured: window.Torras24HAvailability.configured },
    }),
  );
})();
